// ChangeUserDetails
// ------------------
// The Account page's edit dialog: preferred username and passphrase. Both are
// optional, and the user may change either or both in one save.
//
// Close behavior matches SupportTicketModal: a click outside never discards
// half-entered details, deliberate dismissals (X, Cancel, Escape) close
// immediately, and nothing closes mid-save.
//
// Failure reporting is deliberately coarse. The availability answer is
// pool-wide and rate limited server-side, and rejections are mapped from a
// fixed set of sentinel codes, so nothing here echoes raw backend or transport
// text back to the user.

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Eye, EyeOff, KeyRound, Loader2, Lock, UserPen, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import { EMPTY_STRING, PREFERRED_USERNAME_LABEL } from "@/constants/definitions";
import DisplayNameValidationClass from "@/models/displayNameValidation";
import UserClass from "@/models/user";
import PasswordValidationClass from "@/utils/passwordValidation";
import CognitoLoginClass from "@/models/cognitoLogin";
import { normalizeFreeText } from "@/utils/textSanitization";
import { useModalAccessibility } from "@/utils/useModalAccessibility";

const COULD_NOT_UPDATE_PASSWORD_ERROR = "Unable to update passphrase";
const COULD_NOT_UPDATE_DISPLAY_NAME_ERROR =
  "Could not update your preferred username. Please try again.";
const DISPLAY_NAME_TAKEN_ERROR =
  "That preferred username is already taken. Please choose another.";
const DISPLAY_NAME_INVALID_ERROR =
  "That preferred username cannot be used. Please try different characters.";
const CURRENT_PASSPHRASE_REQUIRED_ERROR =
  "Enter your current passphrase to set a new one";
const NEW_PASSPHRASE_REQUIRED_ERROR =
  "Enter a new passphrase, or clear the current passphrase field if you only meant to change your preferred username.";
const UNEXPECTED_ERROR = "An unexpected error occurred. Please try again.";

const PASSPHRASE_RULE_HINT =
  "Minimum 12 characters, with an uppercase letter, a lowercase letter, a number and a symbol";
const PREFERRED_USERNAME_HINT = "Checked for availability when you save";

// Mirrors the backend DISPLAY_NAME_MAX_LENGTH (handlers/userdatasource.py).
// The backend is authoritative; this keeps the value the FE validates and
// submits identical to what will be stored. See SECURITY_AUDIT_FINDINGS F5.
const DISPLAY_NAME_MAX_LENGTH = 100;

// A click outside is the one dismissal that is routinely accidental; every
// other path is deliberate and closes immediately.
type CloseRequestSource = "backdrop" | "dismiss";

interface ChangeUserDetailsProps {
  user?: UserClass;
  show: boolean;
  close: () => void;
  cognitologin: CognitoLoginClass;
  // Called after a change is persisted so the page can re-render the new value.
  onProfileUpdated?: () => void;
}

export default function ChangeUserDetails({
  user,
  show,
  close,
  cognitologin,
  onProfileUpdated,
}: ChangeUserDetailsProps) {
  const currentDisplayName = user?.displayName || "";
  const dialogContainerRef = useRef<HTMLFormElement>(null);

  const [newDisplayName, setNewDisplayName] = useState(currentDisplayName);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [displayNameError, setDisplayNameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // What the dialog would actually do if submitted. Derived ONCE and read by
  // the save handler, the primary button's label, and the backdrop guard, so
  // the label can never promise something the handler will not do.
  //
  // Normalized before comparing, for the same reason the save path normalizes:
  // whitespace the backend would strip is not a change.
  const normalizedDisplayName = normalizeFreeText(newDisplayName, DISPLAY_NAME_MAX_LENGTH);
  const hasDisplayNameChange =
    !!normalizedDisplayName && normalizedDisplayName !== currentDisplayName;

  // ANY passphrase field carrying content counts as intent to change it, not
  // just the new one.
  //
  // Browsers autofill the current-passphrase field readily here, because a
  // password manager sees a password input in a profile form and fills it.
  // Keying intent on the new passphrase alone meant that filled field was
  // silently ignored: entering only a current passphrase closed the dialog
  // having done nothing, and pairing it with a username change quietly
  // updated the username while skipping the passphrase the user believed
  // they were setting. Both looked like the save had worked.
  const hasPassphraseIntent =
    oldPassword.length > 0 || newPassword.length > 0 || confirmPassword.length > 0;

  const hasPendingChanges = hasDisplayNameChange || hasPassphraseIntent;

  const requestClose = useCallback(
    (source: CloseRequestSource) => {
      // Never close mid-save: the calls would still land and the user would not
      // see whether they succeeded.
      if (isLoading) return;
      if (source === "backdrop" && hasPendingChanges) return;
      close();
    },
    [isLoading, hasPendingChanges, close]
  );

  const handleEscapeClose = useCallback(() => requestClose("dismiss"), [requestClose]);

  // Escape closes; focus moves into the dialog on open and back to the trigger
  // on close.
  useModalAccessibility(show, handleEscapeClose, dialogContainerRef);

  // Reset state when the dialog opens. Keyed on `show` alone: including the
  // current name would wipe what the user is typing the moment the profile
  // refreshes underneath them.
  useEffect(() => {
    if (show) {
      setNewDisplayName(user?.displayName || "");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowOldPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setDisplayNameError("");
      setPasswordError("");
      setErrorMessage("");
      setSuccessMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const validateDisplayNameOnBlur = useCallback(async () => {
    if (!newDisplayName || newDisplayName === currentDisplayName) {
      setDisplayNameError("");
      return;
    }
    const validator = new DisplayNameValidationClass();
    // validateDisplayName never throws; it resolves to a message or "".
    const validationResult = await validator.validateDisplayName(newDisplayName, "");
    setDisplayNameError(validationResult);
  }, [newDisplayName, currentDisplayName]);

  const validateNewPasswordOnBlur = useCallback(() => {
    if (!newPassword) return;
    const validator = new PasswordValidationClass();
    const validationResult = validator.validateFirstPassword(newPassword, "", true);
    setPasswordError(validationResult);
  }, [newPassword]);

  const validateConfirmPasswordOnBlur = useCallback(() => {
    if (!confirmPassword) return;
    const validator = new PasswordValidationClass();
    const firstError = validator.validateFirstPassword(newPassword, "", true);
    if (firstError) {
      setPasswordError(firstError);
      return;
    }
    const secondError = validator.validateSecondPassword(newPassword, confirmPassword, firstError);
    setPasswordError(secondError);
  }, [newPassword, confirmPassword]);

  const handleSave = useCallback(async () => {
    if (isLoading) return;
    setErrorMessage("");
    setSuccessMessage("");

    // Nothing to do — the primary button reads "Close" in this state, so this
    // is the path a deliberate click takes.
    if (!hasPendingChanges) {
      close();
      return;
    }

    setIsLoading(true);
    const results: string[] = [];

    // Everything that can reject lives inside this try, including the
    // availability check — it used to sit outside, so a rejection escaped the
    // handler and Save appeared to do nothing at all.
    try {
      if (hasDisplayNameChange) {
        const displayNameValidator = new DisplayNameValidationClass();
        const displayNameValidationResult = await displayNameValidator.validateDisplayName(
          normalizedDisplayName,
          ""
        );
        if (displayNameValidationResult) {
          setDisplayNameError(displayNameValidationResult);
          setIsLoading(false);
          return;
        }
      }

      if (hasPassphraseIntent) {
        // The autofill case: a current passphrase but nothing to change it to.
        // The message names the missing field AND the way out, because the user
        // most likely never intended a passphrase change at all.
        if (!newPassword) {
          setPasswordError(NEW_PASSPHRASE_REQUIRED_ERROR);
          setIsLoading(false);
          return;
        }
        if (!oldPassword) {
          setErrorMessage(CURRENT_PASSPHRASE_REQUIRED_ERROR);
          setIsLoading(false);
          return;
        }
        const passwordValidator = new PasswordValidationClass();
        const firstError = passwordValidator.validateFirstPassword(newPassword, "", true);
        if (firstError) {
          setPasswordError(firstError);
          setIsLoading(false);
          return;
        }
        const secondError = passwordValidator.validateSecondPassword(
          newPassword,
          confirmPassword,
          firstError
        );
        if (secondError) {
          setPasswordError(secondError);
          setIsLoading(false);
          return;
        }
      }

      // Update the preferred username. Awaited: without it the result is a
      // pending promise, which is always truthy, so every failure was reported
      // to the user as a success.
      if (hasDisplayNameChange && user) {
        const updateResult = await user.updateDisplayName(normalizedDisplayName);
        if (updateResult.outcome !== "updated") {
          if (updateResult.outcome === "taken") {
            setDisplayNameError(DISPLAY_NAME_TAKEN_ERROR);
          } else if (updateResult.outcome === "invalid") {
            setDisplayNameError(DISPLAY_NAME_INVALID_ERROR);
          } else {
            setErrorMessage(COULD_NOT_UPDATE_DISPLAY_NAME_ERROR);
          }
          setIsLoading(false);
          return;
        }
        results.push("Preferred username updated");
      }

      // Update passphrase
      if (hasPassphraseIntent) {
        const passwordChangeResult = await cognitologin.changePassword(oldPassword, confirmPassword);
        if (passwordChangeResult !== EMPTY_STRING) {
          setErrorMessage(passwordChangeResult || COULD_NOT_UPDATE_PASSWORD_ERROR);
          // The username change, if there was one, has already been persisted.
          // Reporting only the failure would leave the user believing nothing
          // happened and re-submitting a name that is now their current one.
          if (results.length > 0) {
            setSuccessMessage(results.join(". "));
            onProfileUpdated?.();
          }
          setIsLoading(false);
          return;
        }
        results.push("Passphrase updated");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }

      setSuccessMessage(results.join(". "));
      onProfileUpdated?.();
    } catch (saveError) {
      // Deliberately generic: nothing from the rejection reaches the user.
      setErrorMessage(UNEXPECTED_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading,
    hasPendingChanges,
    hasDisplayNameChange,
    hasPassphraseIntent,
    normalizedDisplayName,
    oldPassword,
    newPassword,
    confirmPassword,
    user,
    cognitologin,
    close,
    onProfileUpdated,
  ]);

  if (!show) return null;

  const visibleError = errorMessage || displayNameError || passwordError;

  const handleFormSubmit = (submitEvent: React.FormEvent<HTMLFormElement>) => {
    // Enter in any field saves, rather than doing nothing.
    submitEvent.preventDefault();
    handleSave();
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — clicking it is treated as an accidental dismiss. */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => requestClose("backdrop")}
        aria-hidden="true"
      />

      {/* Dialog. Capped at 90vh with a scrolling body so the footer stays
          reachable on short viewports, matching SupportTicketModal. */}
      <form
        ref={dialogContainerRef}
        onSubmit={handleFormSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-profile-title"
        className="relative z-10 flex w-full max-w-md flex-col max-h-[90vh] rounded-xl bg-white shadow-xl border border-gray-200"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 id="edit-profile-title" className="text-lg font-semibold">
            Edit profile
          </h2>
          <button
            type="button"
            onClick={() => requestClose("dismiss")}
            disabled={isLoading}
            aria-label="Close"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Messages */}
          {visibleError && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
            >
              {visibleError}
            </div>
          )}
          {successMessage && (
            <div
              role="status"
              className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700"
            >
              {successMessage}
            </div>
          )}

          {/* Preferred username */}
          <div>
            <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
              <UserPen className="h-4 w-4 text-gray-400" />
              Profile
            </h3>
            <FormField
              label={PREFERRED_USERNAME_LABEL}
              htmlFor="newDisplayName"
              helperText={PREFERRED_USERNAME_HINT}
            >
              <Input
                id="newDisplayName"
                type="text"
                value={newDisplayName}
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                autoComplete="username"
                onChange={(changeEvent) => { setNewDisplayName(changeEvent.target.value); setDisplayNameError(""); }}
                onBlur={validateDisplayNameOnBlur}
                disabled={isLoading}
              />
            </FormField>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Passphrase */}
          <div>
            <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
              <KeyRound className="h-4 w-4 text-gray-400" />
              Change passphrase
            </h3>
            <div className="space-y-3">
              <FormField label="Current passphrase" htmlFor="oldPassword">
                <Input
                  id="oldPassword"
                  type={showOldPassword ? "text" : "password"}
                  value={oldPassword}
                  autoComplete="current-password"
                  onChange={(changeEvent) => { setOldPassword(changeEvent.target.value); setErrorMessage(""); }}
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4" />}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      className="text-gray-300 hover:text-gray-500 transition-colors"
                      aria-label={showOldPassword ? "Hide current passphrase" : "Show current passphrase"}
                      tabIndex={-1}
                    >
                      {showOldPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  }
                />
              </FormField>

              <FormField
                label="New passphrase"
                htmlFor="newPasswordModal"
                helperText={PASSPHRASE_RULE_HINT}
              >
                <Input
                  id="newPasswordModal"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  autoComplete="new-password"
                  onChange={(changeEvent) => { setNewPassword(changeEvent.target.value); setPasswordError(""); }}
                  onBlur={validateNewPasswordOnBlur}
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4" />}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="text-gray-300 hover:text-gray-500 transition-colors"
                      aria-label={showNewPassword ? "Hide new passphrase" : "Show new passphrase"}
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  }
                />
              </FormField>

              <FormField label="Confirm new passphrase" htmlFor="confirmPasswordModal">
                <Input
                  id="confirmPasswordModal"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  autoComplete="new-password"
                  onChange={(changeEvent) => { setConfirmPassword(changeEvent.target.value); setPasswordError(""); }}
                  onBlur={validateConfirmPasswordOnBlur}
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4" />}
                  rightSlot={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-gray-300 hover:text-gray-500 transition-colors"
                      aria-label={showConfirmPassword ? "Hide confirmed passphrase" : "Show confirmed passphrase"}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  }
                />
              </FormField>
            </div>
          </div>
        </div>

        {/* Footer
            The primary button is labelled by what it will actually do. With
            nothing pending it closes, which is what it always did — it just
            said "Save changes" while doing it, so a save that had already
            succeeded still looked unfinished.

            Cancel is dropped in that state rather than sitting beside a button
            that performs the identical action. */}
        <div className="flex shrink-0 items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          {hasPendingChanges && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => requestClose("dismiss")}
              disabled={isLoading}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            size="sm"
            // A stale field error must never block closing; it only blocks a save.
            disabled={isLoading || (hasPendingChanges && (!!displayNameError || !!passwordError))}
            className="bg-blue-600 hover:bg-blue-500 text-white"
          >
            {isLoading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
            ) : hasPendingChanges ? (
              "Save changes"
            ) : (
              "Close"
            )}
          </Button>
        </div>
      </form>
    </div>,
    document.body
  );
}
